import {
  assemblePlanNameDisplay,
  getParentEntityIdProjectDepartmentJobHash,
  objectById,
  priorityLabel,
  searchFieldsHaveValues,
  stripHTML
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import Vue from 'vue'
import _ from 'lodash'
import {createAndDownloadWorksheet} from 'src/utilities/excel'
import {dateBetween, esClient, filterBuilder, multiMatch, parentQuery} from 'src/utilities/ptracSearch'
import {crudifyMutations, resetState, updateResult} from 'src/utilities/vuex2'
import {ptracGetEntity, ptracPost} from 'src/utilities/ptracApi'
import {OK} from 'http-status-codes'
import {dispatch, make} from 'vuex-pathify'

const initialState = {
  query: {
    search: null,
    targetCompletionDate: [],
    dateReceived: [],
    priority: null,
    status: [],
    coordinatorId: null,
    clientId: null,
    clientContactId: null,
    builderId: null,
    builderContactId: null,
    communityId: null,
    departmentIds: [],
    planNameId: null,
    name: null,
    number: null,
    permitNumber: null
  },
  saveQuery: {
    value: false,
    itemsToRemove: ["targetCompletionDate", "dateReceived"]
  },
  results: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)

getters.results = (state) => {
  return state.results === undefined ? [] : state.results
}

getters.itemsToRemove = (state) => {
  return state.saveQuery.itemsToRemove
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'search',
    'priority',
    'status',
    'coordinatorId',
    'clientId',
    'clientContactId',
    'builderId',
    'builderContactId',
    'communityId',
    'planNameId',
    'name',
    'number',
    'permitNumber',
    'departmentIds',
    'targetCompletionDate',
    'dateReceived'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

const mutations = make.mutations(state)
mutations.reset = resetState
crudifyMutations(mutations)

mutations.resetResults = (state) => {
  state.results = []
}

mutations.resetQuery = (state) => {
  for (let f in state.query) {
    Vue.set(state.query, f, initialState.query[f])
  }
}

mutations.cleanQuery = (state) => {
  const textFields = [
    'search',
    'priority',
    'coordinatorId',
    'clientId',
    'clientContactId',
    'builderId',
    'builderContactId',
    'communityId',
    'planNameId',
    'name',
    'number',
    'permitNumber'
  ]
  textFields.forEach(f => {
    if (state.query[f] != null) {
      if (state.query[f].trim().length === 0) {
        Vue.set(state.query, f, initialState.query[f])
      }
    }
  })

  const arrayFields = [
    'targetCompletionDate',
    'dateReceived'
  ]
  arrayFields.forEach(f => {
    if (state.query[f] != null) {
      if (state.query[f].length === 0) {
        Vue.set(state.query, f, initialState.query[f])
      }
    }
  })
}

mutations.delete = (state, jobId) => {
  state.results = _.filter(state.results, (job) => job._id !== jobId)
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async updateSearchResult({state}, job) {
      job.client = null
      job.clientContact = null
      if (job.clientId) {
        const response = await ptracGetEntity('companies/' + job.clientId)
        if (OK === response.status) {
          const client = response.data
          job.client = client;
          if (Array.isArray(client.contacts) && job.clientContactId) {
            const clientContact = client.contacts.find(contact => contact.id === job.clientContactId)
            if (clientContact) {
              job.clientContact = clientContact
            }
          }
        }
      }

      job.builder = null
      job.builderContact = null
      if (job.builderId) {
        const response = await ptracGetEntity('companies/' + job.builderId)
        if (OK === response.status) {
          const builder = response.data
          job.builder = builder
          if (Array.isArray(builder.contacts) && job.builderContactId) {
            const builderContact = builder.contacts.find(contact => contact.id === job.builderContactId)
            if (builderContact) {
              job.builderContact = builderContact
            }
          }
        }
      }

      if (job.community && job.community.id) {
        const response = await ptracGetEntity('communities/' + job.community.id)
        if (OK === response.status) {
          job.community = {
            _id: response.data.id,
            name: response.data.name
          }
        }
      }

      job.planName = null
      if (job.planNameId) {
        const response = await ptracGetEntity('plan-names/' + job.planNameId)
        if (OK === response.status) {
          job.planName = {
            id: job.planNameId,
            name: assemblePlanNameDisplay(response.data)
          }
        }
      }

      job.priorityLabel = priorityLabel(job.priority)
      job.priority = +job.priority // Coerced to number to allow column sorting

      updateResult(state, job)
    },
    async fetchResults({state, commit, rootState, rootGetters}) {
      const projectFields = [
        'name',
        'address.blockNumber',
        'address.buildingNumber',
        'address.floorNumber',
        'address.city',
        'address.county',
        'address.jurisdiction',
        'address.lotNumber',
        'address.legalDescription',
        'address.legalSubdivision',
        'address.phase',
        'address.section',
        'address.state',
        'address.streetName',
        'address.streetNumber',
        'address.suiteNumber',
        'address.zip']

      const jobFields = [
        'priority',
        'coordinatorId',
        'clientId',
        'clientContactId',
        'builderId',
        'builderContactId',
        'planNameId',
        'name',
        'description'
      ]

      const searchBody = {
        size: Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS,
        query: {
          bool: {
            must: [
              {
                match: {
                  _class: Constants.ENTITY_FIELDS.JOB.CLASS
                }
              }
            ],
            should: [],
            filter: []
          }
        }
      }

      // Basic Search
      if (state.query.search) {
        const splitSearchTerms = state.query.search.toLowerCase().trim().split(" ")
        searchBody.query.bool.should.push(multiMatch(jobFields, splitSearchTerms))
        searchBody.query.bool.should.push(parentQuery(Constants.ENTITY_FIELDS.PROJECT.PROJECTS, projectFields, splitSearchTerms))
        searchBody.query.bool['minimum_should_match'] = 1
      }

      // Departments
      if (state.query.departmentIds.length > 0) {
        searchBody.query.bool.filter.push({match: {departmentId: state.query.departmentIds.join(" ")}})
      }

      // Statuses
      if (state.query.status.length > 0) {
        searchBody.query.bool.must.push({match: {status: state.query.status.join(" ")}})
      }

      // Must Match
      if (state.query.number) {
        searchBody.query.bool.must.push({
          wildcard: {
            number: {
              value: `${state.query.number}*`
            }
          }
        })
      }
      if (state.query.permitNumber) {
        searchBody.query.bool.must.push({match: {permitNumber: state.query.permitNumber}})
      }

      // Filter Match
      searchBody.query.bool.filter.push(...filterBuilder(jobFields, state.query))

      // Community - maybe deal with this better. Just trying something
      if (state.query.communityId != null) {
        let criteria = {match: {}}
        criteria.match['community._id.keyword'] = state.query.communityId
        searchBody.query.bool.filter.push(criteria)
      }

      // Date filters
      if (state.query.dateReceived && state.query.dateReceived.length > 0) {
        searchBody.query.bool.filter.push(dateBetween((state.query.dateReceived), Constants.ENTITY_FIELDS.JOB.DATE_RECEIVED))
      }
      if (state.query.targetCompletionDate && state.query.targetCompletionDate.length > 0) {
        searchBody.query.bool.filter.push(dateBetween(state.query.targetCompletionDate, Constants.ENTITY_FIELDS.JOB.TARGET_COMPLETION_DATE))
      }

      if (Constants.ENVIRONMENTS.PRODUCTION !== process.env.VUE_APP_ENV) {
        console.log(JSON.stringify(searchBody, null, 2))
      }

      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      const response = await esClient(esCreds).search({
        index: 'ptrac',
        body: searchBody
      })

      const jobResults = response.hits.hits

      if (jobResults.length > 0) {
        // get project data
        const projectsResponse = await ptracPost('projects/findByIdIn', _.map(jobResults, '_source.projectId'))
        let projects = []
        if (OK === projectsResponse.status) {
          projects = projectsResponse.data
        }

        // get company (Client & Builder) data
        const clientIds = _.map(jobResults, '_source.clientId')
        const builderIds = _.map(jobResults, '_source.builderId')
        const companiesResponse = await ptracPost('companies/findByIdIn', _.compact([...clientIds, ...builderIds]))
        let companies = []
        if (OK === companiesResponse.status) {
          companies = companiesResponse.data
        }

        // get plan name data
        let planNames = []
        const uniquePlanNameIds = _.compact(_.map(jobResults, '_source.planNameId'))

        if (uniquePlanNameIds.length > 0) {
          const planNamesResponse = await ptracPost('plan-names/findByIdIn', uniquePlanNameIds)

          if (OK === planNamesResponse.status) {
            planNames = planNamesResponse.data
          }
        }

        jobResults.forEach((jobResult) => {
          const sourceJob = jobResult._source
          const coordinator = objectById(rootState.users.results, sourceJob.coordinatorId)
          let project = objectById(projects, sourceJob.projectId)
          if (!project) {
            project = {}
          }
          sourceJob.id = jobResult._id
          sourceJob.project = project
          sourceJob.coordinator = _.get(coordinator, 'name')
          sourceJob.priorityLabel = priorityLabel(sourceJob.priority)
          sourceJob.priority = +sourceJob.priority // Coerced to number to allow column sorting
          commit('comments/addIdHash', getParentEntityIdProjectDepartmentJobHash(project.id, sourceJob.departmentId, sourceJob.id), { root: true })

          if (sourceJob.clientId) {
            const client = objectById(companies, sourceJob.clientId)
            if (client) {
              sourceJob.client = client
              if (sourceJob.clientContactId && Array.isArray(client.contacts)) {
                const clientContact = client.contacts.find(contact => contact.id === sourceJob.clientContactId)
                if (clientContact) {
                  sourceJob.clientContact = clientContact
                }
              }
            } else {
              console.error('Unable to load Job Client by ID: ' + sourceJob.clientId)
            }
          }

          if (sourceJob.builderId) {
            const builder = objectById(companies, sourceJob.builderId)
            if (builder) {
              sourceJob.builder = builder
              if (sourceJob.builderContactId && Array.isArray(builder.contacts)) {
                const builderContact = builder.contacts.find(contact => contact.id === sourceJob.builderContactId)
                if (builderContact) {
                  sourceJob.builderContact = builderContact
                }
              }
            } else {
              console.error('Unable to load Job Builder by ID: ' + sourceJob.builderId)
            }
          }

          if (planNames && sourceJob.planNameId) {
            const planName = objectById(planNames, sourceJob.planNameId)
            if (planName) {
              sourceJob.planName = planName
            }
          }
        })
        await dispatch('comments/fetchCountsByParentEntityIdHash')
      }

      commit('results', jobResults)

      return response
    },
    resultsToExcel({state, rootState}) {
      const fileName = 'job-search-results.xlsx'
      const tabName = 'Jobs'
      const header = [
        {
          header: 'Name',
          key: 'name',
          width: 40
        }, {
          header: 'Job Number',
          key: 'number',
          width: 15
        }, {
          header: 'Project Name',
          key: 'projectName',
          width: 40
        }, {
          header: 'Permit Number',
          key: 'permitNumber',
          width: 15
        },{
          header: 'Priority',
          key: 'priority',
          width: 15
        }, {
          header: 'Status',
          key: 'status',
          width: 15
        }, {
          header: 'Elevation',
          key: 'elevation',
          width: 15
        }, {
          header: 'Coordinator',
          key: 'coordinator',
          width: 20
        }, {
          header: 'Client',
          key: 'client',
          width: 25
        }, {
          header: 'Builder',
          key: 'builder',
          width: 25
        }, {
          header: 'Community',
          key: 'community',
          width: 25
        }, {
          header: 'Plan Name',
          key: 'planName',
          width: 25
        }, {
          header: 'Target Completion Date',
          key: 'targetCompletionDate',
          width: 25
        }, {
          header: 'Actual Completion Date',
          key: 'actualCompletionDate',
          width: 25
        }, {
          header: 'Description',
          key: 'description',
          width: 60
        }]
      const jobs = _.map(state.results, '_source')

      const rows = []
      jobs.forEach((job) => {
        let actualCompletionDate = null
        if (job.actualCompletionDate) {
          actualCompletionDate = new Date(job.actualCompletionDate)
        }
        rows.push(
          {
            name: job.name,
            number: job.number,
            projectName: job.project.name,
            permitNumber: job.permitNumber,
            priority: priorityLabel(job.priority),
            status: job.status,
            coordinator: job.coordinator,
            builder: _.get(job, 'builder.name'),
            client: _.get(job, 'client.name'),
            community: _.get(job, 'community.name'),
            planName: assemblePlanNameDisplay(job.planName),
            elevation: job.elevation,
            targetCompletionDate: new Date(job.targetCompletionDate),
            actualCompletionDate: actualCompletionDate,
            description: stripHTML(job.description)
          }
        )
      })

      const user = rootState.security.user

      createAndDownloadWorksheet(fileName, tabName, header, rows, user)
    }
  }
}
